import React, { useRef, useMemo, useEffect, useState } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Stars } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import Planet from "./Planet";
import OrbitalPlanet from "./OrbitalPlanet";
import MilkyWay from "./MilkyWay";
import CosmicBackground from "./CosmicBackground";
import ShootingStars from "./ShootingStars";
import CameraController from "./CameraController";
import { getOrbitPosition } from "../utils/orbitUtils";

// ─── Orbit speeds (rad/s, artistic) ────────────────────────────────────────
const ORBIT_SPEEDS = {
  Mercury: 0.10, Venus: 0.07, Earth: 0.07, Mars:    0.05,
  Moon:    0.06, Jupiter: 0.025, Saturn: 0.015,
  Uranus:  0.009, Neptune: 0.005,
};

const INITIAL_ANGLES = (i) => (i * 2 * Math.PI) / 9;

// ─── Planet definitions with real Keplerian orbital elements ───────────────
//
//  Semi-major axes (a) are artistic/compressed for the portfolio.
//  Eccentricities, inclinations (inc), arguments of perihelion (omega),
//  and ascending nodes (Omega) are real J2000 values so orbits have
//  authentic shapes, tilts, and orientations.
//
//  Radii chosen so no two ellipses visually overlap:
//    Mercury  range [11.9, 18.1]   Moon   range [20.8, 23.2]
//    Venus    range [29.8, 30.2]   Mars   range [38.1, 45.9]
//    Earth    range [54.1, 55.9]   Jupiter[80.8, 89.2]
//    Saturn   range [99.3,110.7]   Uranus [119,  131]
//    Neptune  range [153.6,156.4]
//
const orbitingPlanets = [
  {
    name: "Mercury",    section: "experience",     sectionLabel: "Experience",
    modelPath: "/models/mercury.glb", scale: [1.5,1.5,1.5],
    orbitColor: "#E8906A", labelColor: "#E8906A",
    rotationSpeed: 0.017, axialTilt: 0.03,
    orbitElements: { a: 15,  e: 0.206, inc: 7.005, omega:  29.1, Omega:  48.3 },
    orbitSpeed: ORBIT_SPEEDS.Mercury, initialAngle: INITIAL_ANGLES(0),
  },
  {
    name: "Moon",       section: "projects",       sectionLabel: "Projects",
    modelPath: "/models/moon.glb",    scale: [2,2,2],
    orbitColor: "#C0C0D8", labelColor: "#C0C0D8",
    rotationSpeed: 0.003, axialTilt: 0.12,
    orbitElements: { a: 22,  e: 0.055, inc: 5.145, omega:   0.0, Omega: 200.0 },
    orbitSpeed: ORBIT_SPEEDS.Moon,    initialAngle: INITIAL_ANGLES(1),
  },
  {
    name: "Venus",      section: "skills",         sectionLabel: "Skills",
    modelPath: "/models/venus.glb",   scale: [2,2,2],
    orbitColor: "#E8C878", labelColor: "#E8C878",
    rotationSpeed: -0.004, axialTilt: 0.05,
    orbitElements: { a: 30,  e: 0.007, inc: 3.395, omega:  54.9, Omega:  76.7 },
    orbitSpeed: ORBIT_SPEEDS.Venus,   initialAngle: INITIAL_ANGLES(2),
  },
  {
    name: "Mars",       section: "education",      sectionLabel: "Education",
    modelPath: "/models/mars.glb",    scale: [2,2,2],
    orbitColor: "#D4A880", labelColor: "#D4A880",
    rotationSpeed: 0.010, axialTilt: 0.44,
    orbitElements: { a: 42,  e: 0.093, inc: 1.850, omega: 286.5, Omega:  49.6 },
    orbitSpeed: ORBIT_SPEEDS.Mars,    initialAngle: INITIAL_ANGLES(3),
  },
  {
    name: "Earth",      section: "contact",        sectionLabel: "Contact",
    modelPath: "/models/earth.glb",   scale: [3,3,3],
    orbitColor: "#78C8E8", labelColor: "#78C8E8",
    rotationSpeed: 0.010, axialTilt: 0.41,
    orbitElements: { a: 55,  e: 0.017, inc: 0.000, omega: 102.9, Omega:   0.0 },
    orbitSpeed: ORBIT_SPEEDS.Earth,   initialAngle: INITIAL_ANGLES(4),
  },
  {
    name: "Jupiter",    section: "certifications", sectionLabel: "Certifications",
    modelPath: "/models/jupiter.glb", scale: [3,3,3],
    orbitColor: "#C8A8F0", labelColor: "#C8A8F0",
    rotationSpeed: 0.025, axialTilt: 0.05,
    orbitElements: { a: 85,  e: 0.049, inc: 1.303, omega: 273.9, Omega: 100.5 },
    orbitSpeed: ORBIT_SPEEDS.Jupiter, initialAngle: INITIAL_ANGLES(5),
  },
  {
    name: "Saturn",     section: "associative",    sectionLabel: "Associations",
    modelPath: "/models/saturn.glb",  scale: [2,2,2],
    orbitColor: "#E8D890", labelColor: "#E8D890",
    rotationSpeed: 0.022, axialTilt: 0.47,
    orbitElements: { a: 105, e: 0.054, inc: 2.485, omega: 339.4, Omega: 113.7 },
    orbitSpeed: ORBIT_SPEEDS.Saturn,  initialAngle: INITIAL_ANGLES(6),
  },
  {
    name: "Uranus",     section: "testimonials",   sectionLabel: "Testimonials",
    modelPath: "/models/uranus.glb",  scale: [1.5,1.5,1.5],
    orbitColor: "#90D8D8", labelColor: "#90D8D8",
    rotationSpeed: -0.006, axialTilt: 1.71,
    orbitElements: { a: 125, e: 0.047, inc: 0.773, omega:  97.0, Omega:  74.0 },
    orbitSpeed: ORBIT_SPEEDS.Uranus,  initialAngle: INITIAL_ANGLES(7),
  },
  {
    name: "Neptune",    section: "interests",      sectionLabel: "Interests",
    modelPath: "/models/neptune.glb", scale: [2,2,2],
    orbitColor: "#8090E8", labelColor: "#8090E8",
    rotationSpeed: 0.009, axialTilt: 0.49,
    orbitElements: { a: 155, e: 0.009, inc: 1.769, omega: 276.3, Omega: 131.8 },
    orbitSpeed: ORBIT_SPEEDS.Neptune, initialAngle: INITIAL_ANGLES(8),
  },
];

// ─── OrbitWithLabel ─────────────────────────────────────────────────────────
//
// Renders the elliptical orbit ring with a proper bold-on-hover highlight.
//
// Three meshes:
//   thinGeo  — always-visible thin tube  (r 0.22)  — the default ring
//   boldGeo  — thicker, glowing tube     (r 0.65)  — appears on hover/focus
//   hoverGeo — wide invisible tube       (r 2.5)   — large pointer-event target
//
// Using TubeGeometry instead of <line> because WebGL caps lineWidth at 1 px,
// so opacity changes on a <line> never produce a visibly "bold" stroke.
//
const OrbitWithLabel = ({
  orbitElements, color, isFocused, anyFocused, onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  const { a, e, inc, omega, Omega } = orbitElements;

  // Shared curve points (128 pts is plenty for a smooth ellipse)
  const curvePts = useMemo(() => {
    const pts = [];
    for (let i = 0; i < 128; i++) {
      const nu = (i / 128) * Math.PI * 2;
      const [x, y, z] = getOrbitPosition(a, e, inc, omega, Omega, nu);
      pts.push(new THREE.Vector3(x, y, z));
    }
    return pts;
  }, [a, e, inc, omega, Omega]);

  // Thin ring — always on, low opacity
  const thinGeo = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3(curvePts, true);
    return new THREE.TubeGeometry(curve, 256, 0.09, 4, true);
  }, [curvePts]);

  // Highlight ring — subtle brightening on hover / focus
  const boldGeo = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3(curvePts, true);
    return new THREE.TubeGeometry(curve, 256, 0.22, 5, true);
  }, [curvePts]);

  // Wide invisible tube — generous hover/click target
  const hoverGeo = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3(curvePts, true);
    return new THREE.TubeGeometry(curve, 128, 2.5, 4, true);
  }, [curvePts]);

  const isActive = isFocused || hovered;
  const dimmed   = anyFocused && !isFocused;

  const thinOp = dimmed ? 0.03 : isActive ? 0.0  : 0.14;
  const boldOp = dimmed ? 0.0  : isFocused ? 0.55 : hovered ? 0.42 : 0.0;

  return (
    <group>
      {/* Default thin ring */}
      <mesh geometry={thinGeo}>
        <meshBasicMaterial
          color={color}
          transparent
          opacity={thinOp}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Bold highlight ring — bloom-reactive via AdditiveBlending */}
      <mesh geometry={boldGeo}>
        <meshBasicMaterial
          color={color}
          transparent
          opacity={boldOp}
          depthWrite={false}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Invisible wide tube — pointer-event target */}
      <mesh
        geometry={hoverGeo}
        onPointerOver={(ev) => { ev.stopPropagation(); setHovered(true); }}
        onPointerOut={() => setHovered(false)}
        onClick={onClick}
      >
        <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
};

// ─── Sun glow texture (shared, created once) ────────────────────────────────
const makeSunGlowTex = () => {
  const s = 256, c = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = s;
  const ctx  = canvas.getContext("2d");
  const grad = ctx.createRadialGradient(c, c, 0, c, c, c);
  // Tight falloff — most of the brightness is within 30 % of the radius
  grad.addColorStop(0,    "rgba(255,255,255,0.95)");
  grad.addColorStop(0.18, "rgba(255,220,100,0.65)");
  grad.addColorStop(0.40, "rgba(255,140,30,0.18)");
  grad.addColorStop(0.62, "rgba(255,80,0,0.04)");
  grad.addColorStop(1,    "rgba(255,80,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, s, s);
  return new THREE.CanvasTexture(canvas);
};
const SUN_GLOW_TEX = makeSunGlowTex();

// ─── Galaxy ─────────────────────────────────────────────────────────────────

const Galaxy = ({
  onPlanetClick,
  bloomEnabled = true,
  controlsRef: externalControlsRef,
  onFlyReady,
  onViewFlyReady,
  focusedSection,
}) => {
  const internalControlsRef = useRef();
  const controlsRef = externalControlsRef || internalControlsRef;

  const planetPositionsRef                          = useRef({});
  const [flyTarget, setFlyTarget]                   = useState(null);
  const [isFlying, setIsFlying]                     = useState(false);
  const [frozenPlanetName, setFrozenPlanetName]     = useState(null);
  const pendingPlanetRef                            = useRef(null);

  // Unfreeze all planets when the panel is closed
  useEffect(() => {
    if (!focusedSection) setFrozenPlanetName(null);
  }, [focusedSection]);

  // Full cinematic fly-to — freezes planet + opens panel on arrival
  const flyToPlanet = (planetName) => {
    const planet =
      planetName === "Sun"
        ? { name: "Sun", section: "profile", fallbackPosition: [0, 0, 0] }
        : orbitingPlanets.find((p) => p.name === planetName);
    if (!planet) return;
    pendingPlanetRef.current = planet;
    setFrozenPlanetName(planet.name);
    const livePos = planetPositionsRef.current[planet.name];
    setFlyTarget(livePos ? [...livePos] : planet.fallbackPosition || [0, 0, 0]);
    setIsFlying(true);
  };

  // Tutorial-only fly — moves camera near a planet WITHOUT opening any panel.
  // Pass null to cancel any in-progress fly and stop at current position.
  const flyForView = (planetName) => {
    if (planetName === null) {
      setIsFlying(false);
      setFlyTarget(null);
      return;
    }
    const planet =
      planetName === "Sun"
        ? { name: "Sun", fallbackPosition: [0, 0, 0] }
        : orbitingPlanets.find((p) => p.name === planetName);
    if (!planet) return;
    // pendingPlanetRef stays null → onArrived won't trigger onPlanetClick
    const livePos = planetPositionsRef.current[planet.name];
    setFlyTarget(livePos ? [...livePos] : planet.fallbackPosition || [0, 0, 0]);
    setIsFlying(true);
  };

  useEffect(() => {
    if (onFlyReady) onFlyReady(flyToPlanet);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFlyReady]);

  useEffect(() => {
    if (onViewFlyReady) onViewFlyReady(flyForView);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onViewFlyReady]);

  const handlePlanetClick = (planet) => {
    pendingPlanetRef.current = planet;
    setFrozenPlanetName(planet.name);
    const livePos = planetPositionsRef.current[planet.name];
    setFlyTarget(livePos ? [...livePos] : planet.fallbackPosition || [0, 0, 0]);
    setIsFlying(true);
  };

  const handleFlyArrived = () => {
    setIsFlying(false);
    setFlyTarget(null);
    if (pendingPlanetRef.current) {
      onPlanetClick({ section: pendingPlanetRef.current.section, name: pendingPlanetRef.current.name });
      pendingPlanetRef.current = null;
    }
  };

  return (
    <Canvas
      camera={{ position: [0, 40, 140], fov: 55, far: 15000 }}
      gl={{ toneMappingExposure: 2.0 }}
      style={{
        position: "absolute",
        inset: 0,
        background:
          "radial-gradient(ellipse at 30% 40%, rgba(50,0,90,0.35) 0%, transparent 55%)," +
          "radial-gradient(ellipse at 70% 60%, rgba(90,0,60,0.18) 0%, transparent 45%)," +
          "radial-gradient(ellipse at 50% 20%, rgba(30,0,80,0.25) 0%, transparent 50%), #050508",
      }}
    >
      {/* ── Scene lighting ────────────────────────────────────────────────── */}
      {/* Hemisphere gives soft sky-from-above / ground-from-below fill */}
      <hemisphereLight args={["#1a0a3a", "#0a0510", 0.6]} />
      {/* Raised ambient so the dark side of each planet is still visible */}
      <ambientLight intensity={0.8} />
      {/* Sun — warm orange corona, far reach */}
      <pointLight position={[0, 0, 0]} intensity={6}   distance={900} decay={1.2} color="#FFB040" />
      {/* Sun — bright white close light for inner planets */}
      <pointLight position={[0, 0, 0]} intensity={10}  distance={320} decay={1.5} color="#fff6e0" />

      {/* Stars — three layers for depth: local field, mid-field, deep */}
      <Stars radius={400}  depth={60}  count={20000} factor={7}   saturation={0}   fade />
      <Stars radius={250}  depth={50}  count={3000}  factor={2}   saturation={0.6} fade />
      <Stars radius={5000} depth={600} count={8000}  factor={3.5} saturation={0.4} fade />

      <MilkyWay />

      {/* Extreme zoom-out: distant galaxies, nebulae, comet */}
      <CosmicBackground />

      {/* Shooting stars — visible at all distances */}
      <ShootingStars />

      {/* Elliptical orbit rings — labels are on OrbitalPlanet, not here */}
      {orbitingPlanets.map((p) => (
        <OrbitWithLabel
          key={p.name}
          orbitElements={p.orbitElements}
          color={p.orbitColor}
          isFocused={frozenPlanetName === p.name}
          anyFocused={!!frozenPlanetName}
          onClick={() => handlePlanetClick(p)}
        />
      ))}

      {/* Sun glow halo — kept within Mercury's perihelion (r ≈ 11.9 units) */}
      <sprite scale={[26, 26, 1]}>
        <spriteMaterial
          map={SUN_GLOW_TEX}
          color="#FFB040"
          transparent
          opacity={0.88}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </sprite>

      {/* Sun */}
      <Planet
        modelPath="/models/sun.glb"
        position={[0, 0, 0]}
        scale={[5, 5, 5]}
        section="profile"
        name="The Sun"
        rotationSpeed={0.004}
        axialTilt={0.13}
        frozen={frozenPlanetName === "Sun"}
        onClick={() => handlePlanetClick({ name: "Sun", section: "profile", fallbackPosition: [0, 0, 0] })}
      />

      {/* Orbiting planets — labels live here so Billboard has correct world origin */}
      {orbitingPlanets.map((planet) => (
        <OrbitalPlanet
          key={planet.name}
          orbitElements={planet.orbitElements}
          orbitSpeed={planet.orbitSpeed}
          initialAngle={planet.initialAngle}
          positionCallback={(pos) => { planetPositionsRef.current[planet.name] = pos; }}
          modelPath={planet.modelPath}
          scale={planet.scale}
          section={planet.section}
          rotationSpeed={planet.rotationSpeed}
          axialTilt={planet.axialTilt}
          frozen={frozenPlanetName === planet.name}
          name={planet.name}
          labelColor={planet.labelColor}
          sectionLabel={planet.sectionLabel}
          onClick={() => handlePlanetClick(planet)}
        />
      ))}

      <CameraController
        targetPosition={flyTarget}
        isAnimating={isFlying}
        onArrived={handleFlyArrived}
        controlsRef={controlsRef}
      />

      <OrbitControls
        ref={controlsRef}
        autoRotate={!isFlying && !focusedSection}
        autoRotateSpeed={0.3}
        enabled={!isFlying}
        enableZoom
        enablePan
        maxDistance={9000}
        minDistance={3}
        enableDamping
        dampingFactor={0.25}
        rotateSpeed={0.5}
        zoomSpeed={1.2}
        panSpeed={0.5}
      />

      {bloomEnabled && (
        <EffectComposer>
          <Bloom luminanceThreshold={0.22} luminanceSmoothing={0.85} intensity={1.6} mipmapBlur />
        </EffectComposer>
      )}
    </Canvas>
  );
};

export default Galaxy;
